import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import qs from 'query-string';
import alkemyLogo from '../../assets/alkemy/logo.png';
import { t } from '../../i18n/i18n';
import { FORGOTPASSWORD, REGISTER, SIGNIN } from '../../constants/routes';
import LoginForm from '../../components/LoginForm/LoginForm';
import gmailIcon from '../../assets/login/gmail-icon.svg';
import { auth0Client } from '../../auth0/auth0';
import HorizontalLoader from '../../components/HorizontalLoader/HorizontalLoader';
import { Window } from '../../utils/window';
import { Auth0Error } from '../../constants/enums';
import './LoggedOutLayout.scss';
import { setLoginLoading, setVerify } from '../../redux/actions/authActions';
import { resendEmailVerifyAccount } from '../../API/services/authService';
import CalendarIcon from '../../assets/login/CalendarIcon';
import ForgotPasswordForm from '../../components/ForgotPasswordForm/ForgotPasswordForm';
import { debounce } from 'lodash';
import { getLoginNotesInfo } from '../../API/services/loginNotesService';

const LoggedOutLayout = () => {
  const { search } = useLocation();
  const [haveAccount, setHaveAccount] = useState(false);
  const [recoverPassword, setRecoverPassword] = useState(false);
  const [errorKey, setErrorKey] = useState();
  const dispatch = useDispatch();
  const [notesInfo, setNotesInfo] = useState('');
  const [currentNote, setCurrentNote] = useState('');
  const history = useHistory();
  const { loginLoading, isVerify, authenticated, emailUserVerify } = useSelector(
    (state) => state.auth
  );
  const location = useLocation();
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (location.hash) {
      const queryParams = qs.parse(location.hash?.split('#')[1]);
      const actions = {
        [Auth0Error.VALIDATE_EMAIL]: () => {
          dispatch(setLoginLoading(false));
        },
        [Auth0Error.LINKING_ACCOUNTS]: () =>
          notification.open({
            message: t('LINKING_ACCOUNTS_NOTIFICATION'),
            type: 'success',
          }),
        [Auth0Error.USER_EXISTS]: () => {
          dispatch(setLoginLoading(false));
          notification.open({
            message: t('DUPLICATED_EMAIL_VALIDATION_ERROR'),
            type: 'error',
          });
          history.push({
            hash: '',
          });
        },
        [Auth0Error.USER_EXISTS_GOOGLE]: () => {
          dispatch(setLoginLoading(false));
          notification.open({
            message: t('DUPLICATED_EMAIL_VALIDATION_ERROR_GOOGLE'),
            type: 'error',
          });
          history.push({
            hash: '',
          });
        },
        [Auth0Error.USER_EXISTS_PASSWORD]: () => {
          dispatch(setLoginLoading(false));
          notification.open({
            message: t('DUPLICATED_EMAIL_VALIDATION_ERROR_PASSWORD'),
            type: 'error',
          });
          history.push({
            hash: '',
          });
        },
        [Auth0Error.EMAIL_COMPANY_MAIL_REQUIRED]: () => {
          dispatch(setLoginLoading(false));
          notification.open({
            message: t('EMAIL_COMPANY_MAIL_REQUIRED'),
            type: 'error',
          });
          history.push({
            hash: '',
          });
        },
      };
      if (Auth0Error[queryParams.error_description])
        actions[Auth0Error[queryParams.error_description]]();
    }
  }, [location.hash]);

  const singOut = () => {
    dispatch(setVerify());
    if (Window()) {
      localStorage.clear();
      auth0Client().logout({ returnTo: `${window.location.origin}/login` });
    }
  };

  useEffect(() => {
    if (location.pathname === SIGNIN) {
      setHaveAccount(true);
      setRecoverPassword(false);
      if (notesInfo) setCurrentNote(notesInfo?.find(note => note?.flow === SIGNIN));
    }
    if (location.pathname === FORGOTPASSWORD) {
      setHaveAccount(true);
      setRecoverPassword(true);
    }
    if (location.pathname === REGISTER) {
      setHaveAccount(false);
      setRecoverPassword(false);
      if (notesInfo) setCurrentNote(notesInfo?.find(note => note?.flow === REGISTER));
    };
  }, [location.pathname]);

  useEffect(() => {

    const getLoginNotes = async () => {
      try {
        const response = await getLoginNotesInfo();
        setNotesInfo(response);
        setCurrentNote(response?.find(note => note?.flow === location.pathname));
      } catch (error) {
        throw error;
      };
    };

    getLoginNotes();

  }, []);

  useEffect(() => {
    const queryParam = qs.parse(search);

    if (queryParam?.error) {
      auth0Client.logout({
        returnTo: `${window.location.origin}${SIGNIN}?custom_error=${queryParam?.error_description}`,
      });
    }

    if (queryParam?.custom_error) {
      setErrorKey(queryParam.custom_error);
    }
  }, [search]);

  const loginWithGoogle = () => {
    if (Window()) {
      auth0Client().authorize({
        connection: 'google-oauth2',
      });
    }
  };

  const readArticle = (url) => {
    window.open(url);
  };

  const startCountdown = () => {
    setIsDisabled(true);
    setCountdown(60);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendEmailVerifyAccount = () => {
    try {
      resendEmailVerifyAccount();
      notification.open({
        message: t('RESEND_EMAIL_VERIFY_ACCOUNT_SUCCES'),
        type: 'success',
      });
    } catch (error) {
      notification.open({
        message: t('RESEND_EMAIL_VERIFY_ACCOUNT_ERROR'),
        icon: <i className="fas fa-times" style={{ color: 'red' }} />,
      });
    }
  };

  const handleVerifyAccount = debounce(() => {
    startCountdown();
    sendEmailVerifyAccount();
  }, 500);

  return (
    <div className="container-fluid">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="position-absolute bottom-0 left-0 z-0 decoration-bottom"
        viewBox="0 0 1440 290"
      >
        <path
          fill="#4987be"
          fillOpacity="1"
          d="M0,288L48,277.3C96,267,192,245,288,229.3C384,213,480,203,576,197.3C672,192,768,192,864,202.7C960,213,1056,235,1152,234.7C1248,235,1344,213,1392,202.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        />
      </svg>
      {
        haveAccount && recoverPassword ?
          <div className="logged-out-container d-flex justify-content-center align-items-center">
            <div className="d-flex flex-column p-4 px-md-2 forgot-password-container justify-content-around bg-light border shadow">
              <div className="d-flex flex-column">
                <img
                  src={alkemyLogo}
                  width="200px"
                  className="login-content-margin mx-auto"
                  alt="logo-alkemy"
                  data-testid="ly-logo-img"
                />
                <h5
                  className="mx-auto  logged-out-layout-subtitle px-4 mt-2 mx-2 text-base mb-2 w-100 text-center text-sm"
                  data-testid="ly-login-subtitle-h5"
                >
                  {t('LOGGED_OUT_LOGIN_SUBTITLE')}
                </h5>
              </div>
              <div className="px-2 login-content-margin">
                <h4
                  className="mb-3 text-center font-weight-bold text-xxl my-4"
                  data-testid="ly-login-title-h4"
                >
                  {t('RECOVER_PASSWORD_TITLE')}
                </h4>
                <span className="text-center mb-4  text-xs" data-testid="recover-password-disclaimer-span">
                  {t('RECOVER_PASSWORD_DISCLAIMER')}
                </span>
              </div>
              <ForgotPasswordForm />
              {loginLoading && (
                <div className="d-flex justify-content-center">
                  <HorizontalLoader className="h-auto my-4" />
                </div>
              )}
              {!recoverPassword && (
                <div className="w-90 mx-auto mt-3 d-flex justify-content-center align-items-center">
                  <CalendarIcon />
                  <div className="d-flex flex-column ml-4">
                    <a
                      href={process.env.RAZZLE_RUNTIME_SALES_CALENDAR_URL}
                      target="_blank"
                      rel="noreferrer"
                      className=" text-blue-principal font-weight-700 text-lg"
                    >
                      {t('SCHEDULE_MEETING_ACTION')}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
          :
          (!isVerify && authenticated) ? (
            <div className="pt-3 text-center login-content-margin">
              <h4
                className=" text-center font-weight-bold text-title mt-5"
                data-testid="ly-verify-email-title-h4"
              >
                {t('LOGGED_OUT_LAYOUT_VERIFY_EMAIL_TITLE')}
              </h4>
              <h5
                className=" text-center mt-4 font-weight-600 text-xs gray-subtitle"
                data-testid="ly-verify-email-subtitle-h5"
              >
                {t('LOGGED_OUT_LAYOUT_VERIFY_EMAIL_SUBTITLE_FIRST')}
                <span
                  className=" font-weight-bold text-xs text-dark"
                  data-testid="ly-user-email-span"
                >
                  {emailUserVerify}
                </span>
                {t('LOGGED_OUT_LAYOUT_VERIFY_EMAIL_SUBTITLE_SECOND')}
              </h5>
              <p
                className=" text-center mt-4 font-weight-600 text-xs gray-subtitle"
                data-testid="ly-send-email-text-p"
              >
                {t('LOGGED_OUT_LAYOUT_VERIFY_EMAIL_SEND_EMAIL')}
                <button
                  type="button"
                  className="bg-transparent outline-none border-0  font-weight-600 text-xs text-blue-principal"
                  onClick={handleVerifyAccount}
                  data-testid="ly-send-email-btn"
                  disabled={isDisabled}
                >
                  {isDisabled
                    ? `${t('WAIT_TO_RESEND_EMAIL_VERIFY')} ${countdown}s`
                    : t('LOGGED_OUT_LAYOUT_VERIFY_EMAIL_SEND_EMAIL_LINK')}
                </button>
              </p>
              <button
                type="button"
                className="bg-maya-blue rounded-0 px-3 py-1 outline-none border-0  font-weight-600 text-xs text-white mt-3"
                onClick={singOut}
                data-testid="ly-logout-back-login-btn"
              >
                {t('LOGGED_OUT_LAYOUT_VERIFY_BACK_LOGIN')}
              </button>
            </div>
          )
            :
            <div className="row no-gutter flex-row-reverse logged-out-container">
              <div
                data-testid="layout-image-container"
                className="col-lg-6 d-none d-lg-flex justify-content-center align-items-center logged-out-bg-image"
              >
                {
                  currentNote ?
                    <div className="d-flex flex-column p-4 px-md-2 steps-image-info-container justify-content-around align-items-center bg-transparent">
                      <div className="d-flex flex-column">
                        <h2
                          className="mb-3 text-center font-weight-bold text-xxxl my-4"
                          data-testid="ly-login-title-h2"
                        >
                          {currentNote?.title}
                        </h2>
                        <h5
                          className="mx-auto  logged-out-layout-subtitle px-4 mt-2 mx-2 text-base mb-2 w-100 text-center text-sm"
                          data-testid="ly-login-subtitle-h5"
                        >
                          {currentNote?.text}
                        </h5>
                      </div>
                      <img
                        src={currentNote?.imageUrl}
                        className="img-more-info"
                        alt="img-more-info"
                        data-testid="ly-logo-img"
                      />
                      {currentNote?.hasButton && <button
                        data-testid="READ_ARTICLE"
                        onClick={() => readArticle(currentNote?.buttonUrl)}
                        type="submit"
                        className={`rounded-sm border-0 text-white text-xs font-weight-bold w-25 login-form-button bg-maya-blue`}
                      >
                        {t('READ_NOTE_INFO')}
                      </button>}
                    </div>
                    :
                    <p></p>
                }
              </div>
              <div className="col-lg-6 logged-out-children-container d-flex justify-content-center align-items-center">
                <div className="d-flex flex-column p-4 px-md-2 steps-children-loggedout-container justify-content-around bg-light border shadow">
                  <div className="d-flex flex-column">
                    <img
                      src={alkemyLogo}
                      width="200px"
                      className="login-content-margin mx-auto"
                      alt="logo-alkemy"
                      data-testid="ly-logo-img"
                    />
                    <h5
                      className="mx-auto  logged-out-layout-subtitle px-4 mt-2 mx-2 text-base mb-2 w-100 text-center text-sm"
                      data-testid="ly-login-subtitle-h5"
                    >
                      {t('LOGGED_OUT_LOGIN_SUBTITLE')}
                    </h5>
                  </div>
                  <div className="px-2 login-content-margin">
                    {errorKey ? (
                      <>
                        <h5
                          className=" text-center mt-4 mx-2 login-subtitle mb-4"
                          data-testid={`ly-login-error-${errorKey}-h5`}
                        >
                          {t(`LOGGED_OUT_LAYOUT_LOGIN_ERROR_${errorKey.toUpperCase()}`, true)}
                        </h5>
                        <h5
                          className=" text-center mx-2 mb-4"
                          data-testid="ly-login-error-try-again-h5"
                        >
                          {t('LOGGED_OUT_LAYOUT_LOGIN_ERROR_TRY_AGAIN')}
                        </h5>
                      </>
                    ) : (
                      <>
                        <h4
                          className="mb-3 text-center font-weight-bold text-xxl my-4"
                          data-testid="ly-login-title-h4"
                        >
                          {haveAccount ? t('LOGIN_FORM_TITLE') : t('REGISTER_FORM_TITLE')}
                        </h4>
                        <div className="d-flex justify-content-center">
                          <button
                            type="button"
                            onClick={loginWithGoogle}
                            className="font-weight-500 d-flex justify-content-center align-items-center register-with-google-btn rounded-sm text-xs login-form-button"
                            data-testid="ly-register-with-google-btn"
                          >
                            <img
                              src={gmailIcon}
                              alt={t('GMAIL_ICON')}
                              width="18"
                              height="18"
                              className="mr-2"
                              data-testid="ly-register-with-google-img"
                            />
                            {haveAccount ? t('LOGIN_WITH_GOOGLE') : t('REGISTER_WITH_GOOGLE')}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="d-flex align-items-center my-4">
                    <hr className="flex-grow-1" />
                    <span className="mx-3 text-muted text-sm">
                      {t('HEADER_LOGIN_WITH_GOOGLE')}
                    </span>
                    <hr className="flex-grow-1" />
                  </div>
                  <LoginForm haveAccount={haveAccount} recoverPassword={recoverPassword} setHaveAccount={setHaveAccount} />
                  {!recoverPassword &&
                    loginLoading ?
                    <div className="d-flex justify-content-center">
                      <HorizontalLoader className="h-auto my-4" />
                    </div>
                    :
                    <div className="w-90 mx-auto mt-3 d-flex justify-content-center align-items-center">
                      <CalendarIcon />
                      <div className="d-flex flex-column ml-4">
                        <a
                          href={process.env.RAZZLE_RUNTIME_SALES_CALENDAR_URL}
                          target="_blank"
                          rel="noreferrer"
                          className=" text-blue-principal font-weight-700 text-lg"
                        >
                          {t('SCHEDULE_MEETING_ACTION')}
                        </a>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
      }
    </div>
  );
};

export default LoggedOutLayout;
